import { Dialog, DialogProps, MessageDialog, useDialogManager } from '../components/widgets/dialog';
import React, { FunctionComponent } from 'react';
import HubspotForm from 'react-hubspot-form';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import config from './config';
import { backofficeUrl, T } from './helpers';
import { useSubscriptionData } from './hooks/useSubscriptionData';

type HubspotFormDialogProps = {
    email?: string;
    onFormSubmitted: any;
    indexForm?: number;
};

const OnboardingFormHeader = styled.div`
    h2 {
        font-weight: 700;
        font-size: 32px;
        line-height: 36px;
        text-align: left;
        margin: 0 0 32px;
    }
`;

const SellerFormDataHeader = styled.div`
    h1 {
        font-weight: 700;
        font-size: 24px;
        margin: 0 0 10px;
    }
    h2 {
        font-style: italic;
        font-size: 14px;
        font-weight: normal;
    }
    h3 {
        font-size: 14px;
        font-weight: normal;
        margin: 0 0 25px;
    }
`;

export const OnboardingMultiStepDialog: FunctionComponent<HubspotFormDialogProps> = ({ email, onFormSubmitted, indexForm }) => {
    const HubSpotPortalID = 25176166;
    const OnboardingStepFormIds = ['094e2bce-19a6-4bb5-8c2f-1c7c7241ae73', '698a3fc9-5898-44a9-82b9-04ddb4ae8b85'];

    return indexForm! < OnboardingStepFormIds.length ? (
        <Dialog minHeight={400} showCloseButton={false}>
            <OnboardingFormHeader>
                <h3>Almost there!</h3>
                <h2>Tell us a little bit about yourself</h2>
            </OnboardingFormHeader>
            <HubspotForm
                onFormReady={(form: any) => {
                    form.find('input[name=email]')
                        .val(email ?? '')
                        .change();
                }}
                onFormSubmitted={onFormSubmitted}
                portalId={HubSpotPortalID}
                formId={OnboardingStepFormIds[indexForm!]}
            ></HubspotForm>
        </Dialog>
    ) : null;
};

export const SellerDataFormDialog: FunctionComponent<HubspotFormDialogProps> = ({ onFormSubmitted }) => {
    const HubSpotPortalID = 25176166;
    const FormID = 'b1504a90-93eb-4dce-81a7-944af960ef8c';

    return (
        <Dialog minHeight={400} showCloseButton={false}>
            <SellerFormDataHeader>
                <h1>{T._('Help us support you better', 'Admin')}</h1>
                <h2>{T._('Just a quick question to ensure we’re meeting your needs.', 'Admin')}</h2>
                <h3>
                    {T._(
                        'We want to provide support that fits your needs perfectly. Share a little more with us, and we’ll tailor our support to serve you better.',
                        'Admin'
                    )}
                </h3>
            </SellerFormDataHeader>
            <HubspotForm onFormSubmitted={onFormSubmitted} portalId={HubSpotPortalID} formId={FormID}></HubspotForm>
        </Dialog>
    );
};

const TitleNoIntegrationDialog = styled.h1`
    font-size: 24px;
    font-weight: 700;
    font-size: 24px;
    margin: 0 0 32px;
`;

const ContainerNoIntegrationDialog = styled.div`
    h3 {
        margin: 0 0 16px;
        font-weight: 700;
        font-size: 16px;
    }
`;

type NoIntegrationDialogProps = {
    message?: React.ReactNode;
    titleNoIntegration?: string;
    subTitleNoIntegration?: string;
    productId?: number;
    redirectPage?: string;
} & DialogProps;

export const NoIntegrationDialog: FunctionComponent<NoIntegrationDialogProps> = ({
    message,
    titleNoIntegration,
    subTitleNoIntegration,
    productId,
    redirectPage,
    ...props
}) => {
    const navigate = useNavigate();
    const { closeDialog, currentDialogId } = useDialogManager();
    return (
        <MessageDialog
            {...props}
            titleHtml={
                <TitleNoIntegrationDialog>{titleNoIntegration ? titleNoIntegration : T._('Connect Zakeke to your store', 'Admin')}</TitleNoIntegrationDialog>
            }
            message={
                <ContainerNoIntegrationDialog>
                    <h3>{subTitleNoIntegration ? subTitleNoIntegration : T._('You need to connect a store', 'Admin')}</h3>
                    {message ? message : <label>{T._('You need to integrate Zakeke with your store in order to publish a product live', 'Admin')}</label>}
                </ContainerNoIntegrationDialog>
            }
            buttons={[
                {
                    label: T._('Connect your store now', 'Admin'),
                    onClick: () => {
                        if (currentDialogId) closeDialog(currentDialogId);

                        if (productId) {
                            localStorage.setItem(
                                'pendingAction',
                                JSON.stringify({
                                    type: 'publish-product',
                                    productId: productId,
                                    redirectPage: redirectPage,
                                    ts: Date.now()
                                })
                            );
                        }

                        navigate(backofficeUrl('/connect-shop'));
                    }
                }
            ]}
        />
    );
};

export const useUpgradeDialogs = () => {
    const { showDialog, closeDialog } = useDialogManager();
    const { sellerMarketplace } = useSubscriptionData();

    const showPlanUpgradeDialog = (message: string) => {
        showDialog(
            'plan-upgrade',
            <MessageDialog
                message={
                    sellerMarketplace === 'MICROSOFT_AZURE' ? (
                        <React.Fragment>
                            {message}
                            <br />
                            {T._('Go to your Zakeke app Saas from Azure portal to upgrade the plan.', 'Admin')}
                        </React.Fragment>
                    ) : config.isOnCustomplan ? (
                        T._('It seems you have a custom plan, please contact us at support@zakeke.zendesk.com to ask for changes to your plan', 'Admin')
                    ) : (
                        message
                    )
                }
                buttons={
                    config.isOnCustomplan || sellerMarketplace === 'MICROSOFT_AZURE'
                        ? [
                              {
                                  label: T._('OK', 'Admin'),
                                  outline: false,
                                  onClick: () => closeDialog('plan-upgrade')
                              }
                          ]
                        : [
                              {
                                  label: T._('Cancel', 'Admin'),
                                  outline: true,
                                  onClick: () => closeDialog('plan-upgrade')
                              },
                              {
                                  label: T._('Upgrade plan', 'Admin'),
                                  onClick: () => {
                                      window.open('/' + config.culture + '/Admin/Seller/Subscription', '_blank');
                                      closeDialog('plan-upgrade');
                                  }
                              }
                          ]
                }
            />
        );
    };

    const showAddonUpgradeDialog = (message: string) => {
        showDialog(
            'addon-upgrade',
            <MessageDialog
                message={message}
                buttons={[
                    {
                        label: T._('Cancel', 'Admin'),
                        outline: true,
                        onClick: () => closeDialog('addon-upgrade')
                    },
                    {
                        label: T._('Go to addons page', 'Admin'),
                        onClick: () => {
                            window.open('/' + config.culture + '/Admin/Seller/Addons', '_blank');
                            closeDialog('addon-upgrade');
                        }
                    }
                ]}
            />
        );
    };

    return { showPlanUpgradeDialog, showAddonUpgradeDialog };
};
